// Quiet Sites — the review-and-send-check-in list. Surfaces sites the
// health check flagged as quiet (no back-office login AND no price book
// update for 14+ days) and sends each the "Check-in low activity"
// customer email on a click, or all at once. Nothing goes to a customer
// without an explicit click. Reuses POST /api/customers/:id/send-email
// so signature/BCC/CC/logging match every other send. Test mode
// redirects the send to the logged-in user.

import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext.jsx';

const TEMPLATE_NAME = 'Check-in low activity';

function render(text, site, user) {
  return String(text || '')
    .replace(/\{\{first_name\}\}/g,        site.first_name || 'there')
    .replace(/\{\{company\}\}/g,           site.company || 'your team')
    .replace(/\{\{user_name\}\}/g,         user?.name || '')
    .replace(/\{\{owner_calendly_url\}\}/g, user?.calendly_url || '');
}

export default function QuietSites() {
  const { user } = useAuth();
  const [sites, setSites]       = useState([]);
  const [template, setTemplate] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [testMode, setTestMode] = useState(true);
  const [busy, setBusy]         = useState({});   // site_id -> 'sending'|'sent'|'error'
  const [bulk, setBulk]         = useState(null);  // { done, total } | null

  function load() {
    setLoading(true); setError(null);
    Promise.all([
      axios.get('/api/quiet-sites'),
      axios.get('/api/email-templates'),
    ]).then(([q, t]) => {
      setSites(q.data.sites || []);
      const list = t.data?.templates || [];
      setTemplate(list.find((x) => x.name === TEMPLATE_NAME) || null);
    }).catch((e) => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }
  useEffect(load, []);

  async function sendOne(site) {
    if (!template)   throw new Error('Template not found');
    if (!site.email) throw new Error('No customer email on file');
    setBusy((b) => ({ ...b, [site.site_id]: 'sending' }));
    try {
      await axios.post(`/api/customers/${site.customer_id}/send-email`, {
        to: site.email,
        subject: render(template.subject, site, user),
        body: render(template.body, site, user),
        template_id: template.id,
        template_name: template.name,
        test_mode: testMode,
        bcc_sender: true,
      });
      if (!testMode) {
        await axios.post(`/api/quiet-sites/${site.site_id}/checkin-sent`);
        setSites((s) => s.filter((x) => x.site_id !== site.site_id));
      }
      setBusy((b) => ({ ...b, [site.site_id]: 'sent' }));
    } catch (e) {
      setBusy((b) => ({ ...b, [site.site_id]: 'error' }));
      throw e;
    }
  }

  async function sendAll() {
    const list = sites.filter((s) => s.email);
    if (!list.length) return;
    if (!testMode && !window.confirm(`Send the check-in to all ${list.length} customer(s)? This emails them for real.`)) return;
    setBulk({ done: 0, total: list.length });
    for (let i = 0; i < list.length; i++) {
      try { await sendOne(list[i]); } catch (_) { /* keep going, row shows error */ }
      setBulk({ done: i + 1, total: list.length });
    }
    setBulk(null);
    if (!testMode) load();
  }

  const withEmail = useMemo(() => sites.filter((s) => s.email).length, [sites]);

  if (loading) return <section className="page"><h2>Quiet Sites</h2><p>Loading…</p></section>;
  if (error)   return <section className="page"><h2>Quiet Sites</h2><p style={{ color: 'var(--color-bad)' }}>Error: {error}</p></section>;

  return (
    <section className="page">
      <div className="page-header" style={{ marginBottom: 'var(--space-3)' }}>
        <h2 style={{ margin: 0 }}>Quiet Sites <span style={{ fontSize: 12, color: 'var(--color-text-dim)' }}>No back-office login + no price book update for 14+ days</span></h2>
        <div style={{ fontSize: 12, color: 'var(--color-text-dim)', marginTop: 4 }}>
          {sites.length} site{sites.length === 1 ? '' : 's'} awaiting a check-in. Sends the “{TEMPLATE_NAME}” email to the customer — nothing goes out without your click.
        </div>
      </div>

      <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap', marginBottom: 'var(--space-3)' }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input type="checkbox" checked={testMode} onChange={(e) => setTestMode(e.target.checked)} />
          Test mode (send only to me)
        </label>
        <button className="btn-primary" onClick={sendAll} disabled={!template || withEmail === 0 || !!bulk}>
          {bulk ? `Sending ${bulk.done}/${bulk.total}…` : `Send check-in to all (${withEmail})`}
        </button>
        {!template && <span style={{ color: 'var(--color-bad)', fontSize: 12 }}>Template “{TEMPLATE_NAME}” not found — run seed-check-in-low-activity-template.js --apply.</span>}
      </div>

      <div style={{ overflow: 'auto', border: '1px solid var(--color-border)', borderRadius: 6, background: 'var(--color-surface)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead style={{ background: 'var(--color-surface-sunken, #f8fafc)' }}>
            <tr>
              <th style={{ padding: '6px 10px', textAlign: 'left' }}>Customer</th>
              <th style={{ padding: '6px 10px', textAlign: 'left' }}>Site</th>
              <th style={{ padding: '6px 10px', textAlign: 'left' }}>Contact</th>
              <th style={{ padding: '6px 10px', textAlign: 'left' }}>Email</th>
              <th style={{ padding: '6px 10px', textAlign: 'left' }}>Last Login</th>
              <th style={{ padding: '6px 10px', textAlign: 'left' }}>Last Price Book</th>
              <th style={{ padding: '6px 10px' }}></th>
            </tr>
          </thead>
          <tbody>
            {sites.map((s) => {
              const st = busy[s.site_id];
              return (
                <tr key={s.site_id} style={{ borderTop: '1px solid var(--color-border)' }}>
                  <td style={{ padding: '6px 10px' }}>{s.company}</td>
                  <td style={{ padding: '6px 10px' }}>{s.site_name} <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--color-text-dim)', fontSize: 11 }}>{s.site_number}</span></td>
                  <td style={{ padding: '6px 10px' }}>{[s.first_name, s.last_name].filter(Boolean).join(' ') || '—'}</td>
                  <td style={{ padding: '6px 10px', fontSize: 12 }}>{s.email || <span style={{ color: 'var(--color-bad)' }}>no email</span>}</td>
                  <td style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)', fontSize: 12 }}>{s.last_login || 'never'}</td>
                  <td style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)', fontSize: 12 }}>{s.last_pricebook || 'never'}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right' }}>
                    <button
                      className="btn-secondary btn-sm"
                      disabled={!template || !s.email || st === 'sending' || st === 'sent' || !!bulk}
                      onClick={() => sendOne(s).catch(() => {})}
                    >
                      {st === 'sending' ? 'Sending…' : st === 'sent' ? (testMode ? 'Test sent' : 'Sent ✓') : st === 'error' ? 'Retry' : 'Send check-in'}
                    </button>
                  </td>
                </tr>
              );
            })}
            {sites.length === 0 && (
              <tr><td colSpan={7} style={{ padding: 20, textAlign: 'center', color: 'var(--color-text-dim)' }}>No quiet sites awaiting a check-in.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
